import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { FindPendingMappingQueryResponse } from "../types/FindPendingMapping"

type FindPendingMappingClient = typeof client<
  FindPendingMappingQueryResponse,
  Error,
  never
>
type FindPendingMapping = {
  data: FindPendingMappingQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindPendingMappingClient>>
  client: {
    parameters: Partial<Parameters<FindPendingMappingClient>[0]>
    return: Awaited<ReturnType<FindPendingMappingClient>>
  }
}
export const findPendingMappingQueryKey = () =>
  [{ url: "/v1/client-settings/find-pending-mapping" }] as const
export type FindPendingMappingQueryKey = ReturnType<
  typeof findPendingMappingQueryKey
>
export function findPendingMappingQueryOptions<
  TData = FindPendingMapping["response"],
  TQueryData = FindPendingMapping["response"],
>(
  options: FindPendingMapping["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindPendingMapping["response"],
    FindPendingMapping["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findPendingMappingQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindPendingMapping["data"],
        FindPendingMapping["error"]
      >({
        method: "get",
        url: `/v1/client-settings/find-pending-mapping`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find pending mapping
 * @link /v1/client-settings/find-pending-mapping
 */
export function useFindPendingMapping<
  TData = FindPendingMapping["response"],
  TQueryData = FindPendingMapping["response"],
  TQueryKey extends QueryKey = FindPendingMappingQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindPendingMapping["response"],
        FindPendingMapping["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindPendingMapping["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindPendingMapping["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findPendingMappingQueryKey()
  const query = useQuery<
    FindPendingMapping["data"],
    FindPendingMapping["error"],
    TData,
    any
  >({
    ...findPendingMappingQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindPendingMapping["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
